import { useEffect, useMemo, useState } from 'react'

import { cn } from 'src/shared/utils/cn'

import WarningIcon from '../../components/warning-icon'
import { FOOTER_DISCLAIMER_TEXT } from '../../constants/ul-copy'
import { useUploadLeadsContext } from '../../context/upload-leads-context'
import { useUploadLeadsStore } from '../../store/upload-leads-store'

type ClosedDealsChoice = 'yes' | 'no'

export default function ClosedDealsStep() {
  const { setFooterConfig } = useUploadLeadsContext()
  const formData = useUploadLeadsStore((state) => state.formData)
  const setFormData = useUploadLeadsStore((state) => state.setFormData)
  const [selectedChoice, setSelectedChoice] = useState<ClosedDealsChoice | null>(() => {
    if (formData.closedDeals === true) return 'yes'
    if (formData.closedDeals === false) return 'no'
    return null
  })

  const isSelectionMissing = useMemo(() => selectedChoice === null, [selectedChoice])

  const handleChoiceSelect = (choice: ClosedDealsChoice) => {
    setSelectedChoice(choice)
    setFormData({
      ...formData,
      closedDeals: choice === 'yes',
    })
  }

  useEffect(() => {
    setFooterConfig({
      isPrimaryDisabled: isSelectionMissing,
      bottomLeftContent: {
        variant: 'icon-text',
        text: FOOTER_DISCLAIMER_TEXT,
        icon: <WarningIcon />,
      },
    })

    return () => {
      setFooterConfig(null)
    }
  }, [isSelectionMissing, setFooterConfig])

  return (
    <div className="flex size-full items-center justify-center px-5">
      <div className="mx-auto w-full max-w-[500px]">
        <div className="flex flex-col items-center">
          <div className="w-full max-w-[460px] text-center">
            <h1 className="text-[36px] font-semibold leading-tight tracking-[-0.36px] text-primary">
              Are these closed deals?
            </h1>
            <p className="mt-2 text-[18px] font-medium leading-[1.6] text-muted">
              This helps Lynk know which workflows to kick off
              {/* If the file includes both closed and active businesses, please upload them separately. */}
            </p>
          </div>

          <div className="mt-10 flex w-full gap-[15px]">
            <button
              type="button"
              className={cn(
                'flex h-20 flex-1 items-center justify-center rounded-[20px] px-10 py-[30px] text-[20px] font-medium leading-[1.4] tracking-[-0.36px]',
                selectedChoice === 'yes' ? 'bg-primary text-white' : 'bg-accent text-primary',
              )}
              onClick={() => handleChoiceSelect('yes')}
              aria-pressed={selectedChoice === 'yes'}
            >
              Yes
            </button>
            <button
              type="button"
              className={cn(
                'flex h-20 flex-1 items-center justify-center rounded-[20px] px-10 py-[30px] text-[20px] font-medium leading-[1.4] tracking-[-0.36px]',
                selectedChoice === 'no' ? 'bg-primary text-white' : 'bg-accent text-primary',
              )}
              onClick={() => handleChoiceSelect('no')}
              aria-pressed={selectedChoice === 'no'}
            >
              No
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
